import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ExpiredSignupPage } from '../components/ExpiredSignupPage';
import { SignupForm } from '../components/SignupForm/SignupForm';
import { useStudioUnauthenticatedContext } from '../context/StudioContext';
import { AuthPage } from '../page-templates/AuthPage';

export const SignupPage: React.FC = () => {
  const { currentUser, backendClient } = useStudioUnauthenticatedContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get token from URL parameters and validate it
  useEffect(() => {
    const tokenParam = searchParams.get('token');

    if (tokenParam && tokenParam.length > 0) {
      setToken(tokenParam);
      setIsLoading(true);
      setError(null);

      // Call the validate-token endpoint to get user info
      backendClient
        .validateToken(tokenParam)
        .then(({ firstName, email }) => {
          setFirstName(firstName);
          setEmail(email);
        })
        .catch((err) => {
          console.error('Token validation failed:', err);
          setError('Invalid or expired token. Please contact support.');
          setToken(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setError('Invalid or expired token. Please contact support.');
      setToken(null);
    }
  }, [searchParams, backendClient]);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const handleSignInClick = () => {
    navigate('/login');
  };

  // Show loading state while validating token
  if (isLoading) {
    return (
      <AuthPage>
        <div>Validating your invitation...</div>
      </AuthPage>
    );
  }

  // Show error state if token validation failed
  if (error) {
    return <ExpiredSignupPage onReturnToLogin={handleSignInClick} />;
  }

  // Don't render anything if we haven't processed the firstName yet
  if (firstName === null) {
    return null;
  }

  return (
    <AuthPage>
      <SignupForm
        firstName={firstName}
        email={email}
        onSignInClick={handleSignInClick}
        token={token}
      />
    </AuthPage>
  );
};
